import { useCallback, useEffect, useRef, useState } from "react"
import type { RefObject } from "react"
import { log } from "helpers/logger"
import { MAX_SAVE_BYTES, isUnbrokeredReport, parseSaveRequest } from "./artifact-save-request"
import type { PendingBatch, PendingSave, RefusalReason } from "./artifact-save-request"

/**
 * Receives a save an artifact asks for, and holds it until the user says yes (APP-3495).
 *
 * This is the first `message` listener in the client, so it is worth being explicit about what
 * establishes trust. It is NOT the origin: the artifact frame is sandboxed without
 * `allow-same-origin`, so its origin is the string "null", exactly like any other null-origin frame
 * on the page. What is checked instead is position in the frame tree — the sender's `parent` must be
 * the panel wrapper this hook was given. `parent` and `contentWindow` are both readable across
 * origins, so this needs no secret and keeps working if the frame ever moves to its own origin.
 *
 * See pi-server docs/adrs/0015 and docs/designs/artifact-brokered-download.md.
 */

export type BrokerRefusal = RefusalReason | "cancelled" | "unbrokered" | "busy" | "save-failed"

/**
 * A ceiling on how much one panel can report. Every message that clears the identity check would
 * otherwise fire an analytics event, and an artifact can post in a loop — which both floods the event
 * stream and makes the `unbrokered` count APP-3535 filters on artifact-writable. Past this, refusals
 * are dropped silently: the save is refused either way, only the reporting stops.
 */
const MAX_REPORTED_REFUSALS = 20

/**
 * How much one prompt can grow to cover. A batch export posts one request per file while the prompt is
 * already up, so the alternative to collecting them is losing them. Bounded because the artifact chooses
 * how many it sends: past these, the extra files are refused and the user is told some were not saved.
 */
const MAX_BATCH_FILES = 10
const MAX_BATCH_BYTES = 2 * MAX_SAVE_BYTES

/**
 * Deliberately says nothing the artifact supplied. An artifact can trigger a refusal on purpose, so
 * echoing its filename here would let it paint text of its choosing into app chrome — "couldn't save
 * invoice-from-your-bank.exe". The confirmation dialog does name the file, because that is its job
 * and the user is the one deciding.
 */
const REFUSAL_NOTICES: Partial<Record<BrokerRefusal, string>> = {
  "media-type": "That artifact tried to save a file type that isn't allowed. Nothing was saved.",
  "too-large": "That artifact tried to save a file that's too large. Nothing was saved.",
  // Says the export failed rather than what went wrong, because the cause is ours, not the user's:
  // the shim could not intercept this artifact's download. Better than a button that does nothing.
  unbrokered: "This artifact's export didn't work. Nothing was saved.",
  // Raised once the prompt is resolved, not while it is up: a batch that outgrew what one prompt covers
  // still has to account for the files it left behind.
  busy: "Some of the artifact's files weren't saved.",
  "save-failed": "That file couldn't be saved.",
}

/** A batch keeps writing past a failure, so some of it may be on disk. Saying otherwise invites a retry. */
const PARTIAL_SAVE_FAILURE_NOTICE = "Some of the artifact's files couldn't be saved."

interface UseArtifactFileBrokerOptions {
  /** The panel wrapper. The artifact frame is nested inside it, and that is what is verified. */
  frameRef: RefObject<HTMLIFrameElement | null>
  onSaved?: (save: PendingSave) => void
  onRefused?: (reason: BrokerRefusal) => void
}

interface UseArtifactFileBrokerResult {
  pending: PendingBatch | null
  refusal: string | null
  confirmSave: () => void
  cancelSave: () => void
  dismissRefusal: () => void
}

export function useArtifactFileBroker({
  frameRef,
  onSaved,
  onRefused,
}: UseArtifactFileBrokerOptions): UseArtifactFileBrokerResult {
  const [pending, setPending] = useState<PendingBatch | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)

  // Read through a ref so the listener registers once, rather than being torn down and rebuilt every
  // time the caller re-renders with fresh callback identities.
  const handlers = useRef({ onSaved, onRefused })
  useEffect(() => {
    handlers.current = { onSaved, onRefused }
  }, [onSaved, onRefused])

  // Mirrors `pending` so both the listener and the confirm path can read it without either
  // re-registering or doing side effects inside a state updater, which React may run twice.
  const pendingRef = useRef<PendingBatch | null>(null)
  const setPendingBatch = useCallback((batch: PendingBatch | null) => {
    pendingRef.current = batch
    setPending(batch)
    // A live prompt replaces an earlier refusal: "nothing was saved" beside a Save dialog reads as
    // contradictory chrome, and an artifact could engineer that pairing on purpose.
    if (batch) setRefusal(null)
  }, [])

  // Requests refused because the batch was already full. Counted rather than discarded so the notice
  // can say so once the prompt is resolved, instead of files vanishing with nothing on screen.
  const droppedWhilePendingRef = useRef(0)
  const reportedRefusalsRef = useRef(0)

  /**
   * Records a refusal and, when nothing is in front of the user, tells them. A notice raised over a
   * live prompt would contradict it and an artifact can provoke one at any moment, so the notice waits
   * while the telemetry does not. A malformed message surfaces nothing either way: no recognisable
   * export was attempted, so there is nothing to report to the user.
   */
  const reportRefusal = useCallback((reason: BrokerRefusal) => {
    if (reportedRefusalsRef.current >= MAX_REPORTED_REFUSALS) return
    reportedRefusalsRef.current += 1

    handlers.current.onRefused?.(reason)
    if (pendingRef.current) return
    const notice = REFUSAL_NOTICES[reason]
    if (notice) setRefusal(notice)
  }, [])

  /**
   * Folded into the prompt already up, rather than queued behind it or dropped. A queue would let an
   * artifact stack dialogs; dropping cost a six-file export five of its files. One prompt covering the
   * set is one decision, and it grows in front of the user rather than behind them — the dialog re-arms
   * every time it does, so Save is never live over a list the user has not had a moment to read.
   */
  const addToBatch = useCallback((save: PendingSave) => {
    const batch = pendingRef.current
    if (!batch) {
      setPendingBatch({ files: [save], totalBytes: save.sizeBytes })
      return
    }

    // Refused rather than accepted without limit: the artifact chooses how many it sends, and the
    // dialog has to stay something a person can actually read before agreeing to it.
    if (batch.files.length >= MAX_BATCH_FILES) {
      droppedWhilePendingRef.current += 1
      reportRefusal("busy")
      return
    }
    if (batch.totalBytes + save.sizeBytes > MAX_BATCH_BYTES) {
      droppedWhilePendingRef.current += 1
      reportRefusal("too-large")
      return
    }

    setPendingBatch({
      files: [...batch.files, save],
      totalBytes: batch.totalBytes + save.sizeBytes,
    })
  }, [setPendingBatch, reportRefusal])

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const wrapperWindow = frameRef.current?.contentWindow ?? null
      // Narrowed rather than cast: `event.source` is also `MessagePort | ServiceWorker`, neither of
      // which has a `parent`, and this is the one value the whole boundary turns on.
      const sender = event.source
      if (!wrapperWindow || !sender || !("parent" in sender) || sender.parent !== wrapperWindow) return

      // Not a save at all: the shim telling us it saw a download it could not broker. Recorded rather
      // than dropped, because this is the only evidence that a skill has drifted off the interception
      // path — no message arrives at all when the shim simply fails to fire.
      if (isUnbrokeredReport(event.data)) {
        reportRefusal("unbrokered")
        return
      }

      const result = parseSaveRequest(event.data)
      if (!result.ok) {
        reportRefusal(result.reason)
        return
      }

      addToBatch(result.save)
    }

    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [frameRef, addToBatch, reportRefusal])

  /**
   * Reads and clears the count of files the prompt could not cover. Cleared on the way out whatever
   * happens next: the count belongs to the batch being resolved, and left standing it surfaced against
   * whichever export came after it.
   */
  const takeDroppedCount = useCallback(() => {
    const dropped = droppedWhilePendingRef.current
    droppedWhilePendingRef.current = 0
    return dropped
  }, [])

  /**
   * Not routed through `reportRefusal`. That cap exists to bound what an *artifact* can provoke, and a
   * failed write is the app's own failure — an artifact that has spent the cap must not be able to
   * silence it, in the notice or in the telemetry.
   */
  const reportSaveFailure = useCallback((failed: number, total: number) => {
    handlers.current.onRefused?.("save-failed")
    setRefusal(failed < total ? PARTIAL_SAVE_FAILURE_NOTICE : REFUSAL_NOTICES["save-failed"] ?? null)
  }, [])

  const confirmSave = useCallback(() => {
    const batch = pendingRef.current
    if (!batch) return
    setPendingBatch(null)
    const dropped = takeDroppedCount()

    // Each file is written independently: one failure must not cost the rest of the batch. The write
    // can fail on the far side of the user's decision — a blocked download, a disk error — and by here
    // the dialog is already gone, so uncaught it loses the file, skips the caller's success event and
    // says nothing. The sibling path does the same (hooks/use-url-download).
    let failed = 0
    for (const save of batch.files) {
      try {
        handlers.current.onSaved?.(save)
      } catch (error) {
        failed += 1
        log(error)
      }
    }

    // A failure outranks the dropped-files notice: it is the more serious of the two, and only one
    // notice can be on screen.
    if (failed > 0) {
      reportSaveFailure(failed, batch.files.length)
      return
    }
    if (dropped > 0) setRefusal(REFUSAL_NOTICES.busy ?? null)
  }, [setPendingBatch, takeDroppedCount, reportSaveFailure])

  const cancelSave = useCallback(() => {
    if (!pendingRef.current) return
    setPendingBatch(null)
    handlers.current.onRefused?.("cancelled")
    if (takeDroppedCount() > 0) setRefusal(REFUSAL_NOTICES.busy ?? null)
  }, [setPendingBatch, takeDroppedCount])

  const dismissRefusal = useCallback(() => setRefusal(null), [])

  return { pending, refusal, confirmSave, cancelSave, dismissRefusal }
}
